import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiApi, handleAiError } from './api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Send, Loader2, MessageCircle, Plus } from 'lucide-react';
import { toast } from 'sonner';
import UsageNudge from './UsageNudge';

const SUGGESTIONS = [
  'I get nervous before big games. What can I do tonight?',
  'I keep replaying mistakes. How do I reset between plays?',
  'How do I build a self-talk routine I will actually use?',
  'How do I help my athlete without adding pressure?',
];

export default function Coach() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [usageTick, setUsageTick] = useState(0);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const newSession = () => {
    setSessionId(null);
    setMessages([]);
    setInput('');
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput('');
    const optimistic = [
      ...messages,
      { role: 'user', content, _id: `tmp-${Date.now()}` },
    ];
    setMessages(optimistic);
    setSending(true);
    try {
      const res = await aiApi.coachChat(content, sessionId);
      if (!sessionId) setSessionId(res.session_id);
      setMessages([...optimistic, { role: 'assistant', content: res.reply, _id: `r-${Date.now()}` }]);
      setUsageTick((n) => n + 1);
    } catch (e) {
      handleAiError(e, toast, navigate);
      setMessages(messages);
      setUsageTick((n) => n + 1);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="coach-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#004C54] font-semibold mb-2">AI Coach</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Mental Performance Chat</h1>
          <p className="text-sm text-foreground/70 mt-1">Talk through pressure, focus, and confidence in plain language.</p>
        </div>
        {messages.length > 0 && (
          <Button onClick={newSession} variant="outline" size="sm" data-testid="new-chat">
            <Plus className="h-4 w-4 mr-1.5" /> New chat
          </Button>
        )}
      </div>

      <UsageNudge kind="coach" refreshKey={usageTick} />

      {messages.length === 0 ? (
        <Card className="border-border">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 text-foreground/70 mb-4">
              <MessageCircle className="h-4 w-4 text-[#004C54]" />
              <span className="text-sm">Try a starter question, or write your own below.</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-left text-sm bg-[#F6F7F8] hover:bg-[#E9EEEE] border border-border/60 rounded-lg px-3.5 py-3 transition-colors"
                  data-testid={`coach-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div
          ref={scrollerRef}
          className="bg-white border border-border rounded-xl p-4 sm:p-5 max-h-[60vh] overflow-y-auto space-y-3"
          data-testid="coach-messages"
        >
          {messages.map((m) => (
            <div key={m._id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[88%] sm:max-w-[78%] rounded-2xl px-4 py-2.5 text-[14.5px] leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-[#004C54] text-white rounded-br-md'
                    : 'bg-[#F4F6F6] text-foreground rounded-bl-md'
                }`}
                data-testid={`message-${m.role}`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-[#F4F6F6] rounded-2xl rounded-bl-md px-4 py-2.5 text-foreground/60 text-sm flex items-center gap-2" data-testid="coach-thinking">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Thinking...
              </div>
            </div>
          )}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="sticky bottom-[80px] md:bottom-4 z-10"
      >
        <div className="bg-white border border-border rounded-xl p-2 shadow-sm flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask anything about your mental game..."
            className="min-h-[44px] max-h-40 resize-none border-0 focus-visible:ring-0 text-[15px]"
            data-testid="coach-input"
          />
          <Button
            type="submit"
            disabled={sending || !input.trim()}
            className="bg-[#004C54] hover:bg-[#003a40] text-white h-11 w-11 p-0"
            data-testid="coach-send"
            aria-label="Send message"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </form>
    </div>
  );
}
